class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.loadingTimer = null;
    this.successTimer = null;

    this.addEventListener("keyup", (event) => {
      if (event.code === "Escape") {
        this.close();
      }
    });

    this.bindOverlay();
    this.setHeaderCartIconAccessibility();

    /*
     * Detect Add To Cart submission
     */
    document.addEventListener(
      "submit",
      (event) => {
        const form = event.target;

        if (!(form instanceof HTMLFormElement)) return;

        const action = form.getAttribute("action") || "";

        const isAddToCartForm =
          action.includes("/cart/add") ||
          form.querySelector('[name="add"]');

        if (!isAddToCartForm) return;

        this.startAddToCartLoading();
      },
      true
    );
  }


  /* =========================================================
     OVERLAY
  ========================================================= */

  bindOverlay() {
    const overlay = this.querySelector("#CartDrawer-Overlay");

    if (!overlay) return;

    overlay.addEventListener(
      "click",
      this.close.bind(this)
    );
  }


  /* =========================================================
     HEADER CART ICON
  ========================================================= */

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.querySelector("#cart-icon-bubble");

    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute(
      "aria-haspopup",
      "dialog"
    );

    cartLink.addEventListener(
      "click",
      (event) => {
        event.preventDefault();

        this.open(cartLink);
      }
    );

    cartLink.addEventListener(
      "keydown",
      (event) => {
        if (
          event.code.toUpperCase() === "SPACE"
        ) {
          event.preventDefault();

          this.open(cartLink);
        }
      }
    );
  }


  /* =========================================================
     CREATE PREMIUM LOADER
  ========================================================= */

  createLoader() {
    let loader =
      this.querySelector(".cart-drawer-premium-loader");

    if (loader) return loader;


    loader = document.createElement("div");

    loader.className =
      "cart-drawer-premium-loader";

    loader.setAttribute(
      "role",
      "status"
    );

    loader.setAttribute(
      "aria-live",
      "polite"
    );

    loader.setAttribute(
      "aria-atomic",
      "true"
    );


    loader.innerHTML = `
      <div class="cart-loader__ambient cart-loader__ambient--one"></div>
      <div class="cart-loader__ambient cart-loader__ambient--two"></div>

      <div class="cart-loader__card">

        <div class="cart-loader__visual">

          <div class="cart-loader__orbit cart-loader__orbit--outer"></div>

          <div class="cart-loader__orbit cart-loader__orbit--inner"></div>

          <div class="cart-loader__core">

            <svg
              class="cart-loader__leaf"
              viewBox="0 0 32 32"
              aria-hidden="true"
            >
              <path
                d="M25.8 5.8C17.2 5.2 10.6 8.1 7.4 13.1C4.7 17.3 5.6 22.4 8.8 25.4C10.6 21.3 13.8 17.6 18.9 14.2C14.8 18.1 12.2 21.8 10.8 25.8C15.4 27.7 20.8 26.1 23.5 22C26.4 17.5 27.1 11.3 25.8 5.8Z"
                fill="currentColor"
              />
            </svg>

            <svg
              class="cart-loader__check"
              viewBox="0 0 32 32"
              aria-hidden="true"
            >
              <path
                d="M8 16.5L13.5 22L24 10.5"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

          </div>

        </div>


        <div class="cart-loader__content">

          <span class="cart-loader__eyebrow">
            The Happy Garden
          </span>

          <h3 class="cart-loader__title">
            Adding to your basket
          </h3>

          <p class="cart-loader__description">
            Just a moment while we prepare your item.
          </p>


          <div
            class="cart-loader__progress"
            aria-hidden="true"
          >
            <span></span>
            <span></span>
            <span></span>
          </div>

        </div>

      </div>
    `;


    const drawer =
      this.querySelector("#CartDrawer");


    if (drawer) {
      drawer.appendChild(loader);
    } else {
      this.appendChild(loader);
    }


    return loader;
  }


  /* =========================================================
     START LOADING
  ========================================================= */

  startAddToCartLoading() {
    /*
     * Don't trigger drawer loader on cart page
     */
    if (
      document.querySelector("cart-items")
    ) {
      return;
    }


    clearTimeout(this.loadingTimer);
    clearTimeout(this.successTimer);


    const loader = this.createLoader();


    loader.classList.remove(
      "is-success"
    );


    const title =
      loader.querySelector(
        ".cart-loader__title"
      );

    const description =
      loader.querySelector(
        ".cart-loader__description"
      );


    if (title) {
      title.textContent =
        "Adding to your basket";
    }


    if (description) {
      description.textContent =
        "Just a moment while we prepare your item.";
    }


    this.classList.add(
      "is-cart-loading"
    );


    /*
     * Open drawer immediately
     */
    if (
      !this.classList.contains("active")
    ) {
      this.open();
    }


    /*
     * Safety timeout
     */
    this.loadingTimer =
      setTimeout(() => {

        this.hideCartLoader();

      }, 12000);
  }


  /* =========================================================
     SUCCESS STATE
  ========================================================= */

  showLoaderSuccess() {
    clearTimeout(this.loadingTimer);


    /*
     * New CartDrawer HTML may have replaced
     * the original loader, so recreate it.
     */
    const loader =
      this.createLoader();


    this.classList.add(
      "is-cart-loading"
    );


    loader.classList.add(
      "is-success"
    );


    const title =
      loader.querySelector(
        ".cart-loader__title"
      );

    const description =
      loader.querySelector(
        ".cart-loader__description"
      );


    if (title) {
      title.textContent =
        "Added to your basket";
    }


    if (description) {
      description.textContent =
        "Your item is ready in the basket.";
    }


    clearTimeout(this.successTimer);


    this.successTimer =
      setTimeout(() => {

        this.hideCartLoader();

        this.animateUpdatedContent();

      }, 550);
  }


  /* =========================================================
     HIDE LOADER
  ========================================================= */

  hideCartLoader() {
    const loader =
      this.querySelector(
        ".cart-drawer-premium-loader"
      );


    if (!loader) {

      this.classList.remove(
        "is-cart-loading"
      );

      return;
    }


    loader.classList.add(
      "is-leaving"
    );


    setTimeout(() => {

      this.classList.remove(
        "is-cart-loading"
      );

      loader.remove();

    }, 280);
  }


  /* =========================================================
     CART CONTENT ANIMATION
  ========================================================= */

  animateUpdatedContent() {
    const inner =
      this.querySelector(
        ".drawer-inner"
      );


    if (!inner) return;


    inner.classList.remove(
      "cart-content-updated"
    );


    void inner.offsetWidth;


    inner.classList.add(
      "cart-content-updated"
    );


    setTimeout(() => {

      inner.classList.remove(
        "cart-content-updated"
      );

    }, 500);
  }


  /* =========================================================
     OPEN
  ========================================================= */

  open(triggeredBy) {
    const isCartPage =
      document.querySelector(
        "cart-items"
      );


    if (isCartPage) return;


    if (triggeredBy) {
      this.setActiveElement(
        triggeredBy
      );
    }


    requestAnimationFrame(() => {

      this.classList.add(
        "animate",
        "active"
      );

    });


    document.body.classList.add(
      "cart-drawer-quick-view"
    );


    if (
      window.matchMedia(
        "(min-width: 768px)"
      ).matches
    ) {

      document.body.style.overflow =
        "hidden";

      document.body.style.paddingRight =
        "4px";
    }


    /*
     * No AOS animation here
     */
  }


  /* =========================================================
     CLOSE
  ========================================================= */

  close() {
    clearTimeout(this.loadingTimer);
    clearTimeout(this.successTimer);


    this.classList.remove(
      "active",
      "is-cart-loading"
    );


    const loader =
      this.querySelector(
        ".cart-drawer-premium-loader"
      );


    if (loader) {
      loader.remove();
    }


    if (
      typeof removeTrapFocus ===
      "function"
    ) {

      removeTrapFocus(
        this.activeElement
      );
    }


    document.body.classList.remove(
      "cart-drawer-quick-view"
    );


    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  }


  /* =========================================================
     SUMMARY ACCESSIBILITY
  ========================================================= */

  setSummaryAccessibility(cartDrawerNote) {
    if (!cartDrawerNote) return;


    cartDrawerNote.setAttribute(
      "role",
      "button"
    );


    cartDrawerNote.setAttribute(
      "aria-expanded",
      "false"
    );


    const nextElement =
      cartDrawerNote.nextElementSibling;


    if (
      nextElement &&
      nextElement.getAttribute("id")
    ) {

      cartDrawerNote.setAttribute(
        "aria-controls",
        nextElement.id
      );

    }


    cartDrawerNote.addEventListener(
      "click",
      (event) => {

        const details =
          event.currentTarget.closest(
            "details"
          );


        if (!details) return;


        event.currentTarget.setAttribute(
          "aria-expanded",
          !details.hasAttribute("open")
        );

      }
    );


    if (
      cartDrawerNote.parentElement &&
      typeof onKeyUpEscape ===
        "function"
    ) {

      cartDrawerNote.parentElement
        .addEventListener(
          "keyup",
          onKeyUpEscape
        );

    }
  }


  /* =========================================================
     RENDER UPDATED CART
  ========================================================= */

  renderContents(parsedState) {
    this.productId =
      parsedState.id;


    this.getSectionsToRender().forEach(
      (section) => {

        const sectionElement =
          section.selector

            ? document.querySelector(
                section.selector
              )

            : document.getElementById(
                section.id
              );


        if (!sectionElement) return;


        if (
          !parsedState.sections ||
          !parsedState.sections[
            section.id
          ]
        ) {
          return;
        }


        const html =
          this.getSectionInnerHTML(
            parsedState.sections[
              section.id
            ],
            section.selector
          );


        if (html !== null) {

          sectionElement.innerHTML =
            html;

        }

      }
    );


    /*
     * Re-bind overlay after Shopify
     * replaces drawer HTML
     */
    this.bindOverlay();


    const drawerInner =
      this.querySelector(
        ".drawer-inner"
      );


    if (drawerInner) {

      drawerInner.classList.remove(
        "is-empty"
      );

    }


    requestAnimationFrame(() => {

      this.open();

      this.showLoaderSuccess();

    });
  }


  /* =========================================================
     PARSE SECTION HTML
  ========================================================= */

  getSectionInnerHTML(
    html,
    selector = ".shopify-section"
  ) {

    const parsedDocument =
      new DOMParser()
        .parseFromString(
          html,
          "text/html"
        );


    const element =
      parsedDocument.querySelector(
        selector
      );


    return element
      ? element.innerHTML
      : null;
  }


  /* =========================================================
     SECTIONS
  ========================================================= */

  getSectionsToRender() {
    return [

      {
        id: "cart-drawer",
        selector: "#CartDrawer"
      },

      {
        id: "cart-icon-bubble"
      }

    ];
  }


  /* =========================================================
     GET DOM
  ========================================================= */

  getSectionDOM(
    html,
    selector = ".shopify-section"
  ) {

    return new DOMParser()
      .parseFromString(
        html,
        "text/html"
      )
      .querySelector(
        selector
      );
  }


  setActiveElement(element) {
    this.activeElement = element;
  }
}


if (
  !customElements.get(
    "cart-drawer"
  )
) {

  customElements.define(
    "cart-drawer",
    CartDrawer
  );

}



/* =========================================================
   CART DRAWER ITEMS
========================================================= */

class CartDrawerItems extends CartItems {

  getSectionsToRender() {

    return [

      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer-inner"
      },

      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section"
      }

    ];
  }
}


if (
  !customElements.get(
    "cart-drawer-items"
  )
) {

  customElements.define(
    "cart-drawer-items",
    CartDrawerItems
  );

}